import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useEffect, useState, useRef } from "react";
import { useSwipe } from "@/hooks/useSwipe";

interface Event {
  id: number;
  slug: string;
  title: string;
  caption: string;
  main_image: string;
  date: string;
  location: string;
  description: string;
}

export default function GallerySection() {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [cardsVisible, setCardsVisible] = useState(3);
  const trackRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function updateVisible() {
      if (window.innerWidth < 768) {
        setCardsVisible(1);
      } else if (window.innerWidth < 1024) {
        setCardsVisible(2);
      } else {
        setCardsVisible(3);
      }
    }
    updateVisible();
    window.addEventListener("resize", updateVisible);
    return () => window.removeEventListener("resize", updateVisible);
  }, []);

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        const { data, error: dbError } = await supabase
          .from("events")
          .select("id, slug, title, caption, main_image, date, location, description")
          .eq("status", "active")
          .order("created_at", { ascending: false });

        if (dbError || !data || data.length === 0) {
          setError(true);
        } else {
          setEvents(data);
        }
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  // Inject JSON-LD structured data for AI / search engines
  useEffect(() => {
    if (events.length === 0) return;

    const existingScript = document.getElementById("events-jsonld");
    if (existingScript) existingScript.remove();

    const script = document.createElement("script");
    script.id = "events-jsonld";
    script.type = "application/ld+json";

    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "itemListElement": events.map((event, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "Event",
          "name": event.title,
          "description": event.description || event.caption,
          "image": event.main_image,
          "startDate": event.date || undefined,
          "location": event.location ? {
            "@type": "Place",
            "name": event.location,
          } : undefined,
          "url": `https://entreelas.com.br/evento/${event.slug}`,
        },
      })),
    };

    script.textContent = JSON.stringify(jsonLd);
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById("events-jsonld");
      if (el) el.remove();
    };
  }, [events]);

  useEffect(() => {
    if (trackRef.current) {
      const cardWidth = trackRef.current.scrollWidth / events.length;
      trackRef.current.scrollTo({
        left: currentIndex * cardWidth,
        behavior: "smooth",
      });
    }
  }, [currentIndex, events.length]);

  const maxIndex = Math.max(0, events.length - cardsVisible);

  function next() {
    setCurrentIndex((prev) => Math.min(prev + 1, maxIndex));
  }

  function prev() {
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
  }

  const showNav = events.length > cardsVisible;
  const swipe = useSwipe(next, prev);

  return (
    <section id="galeria" className="w-full bg-white py-16 md:py-24 relative overflow-hidden">
      {/* Colonial azulejo-inspired thin border top */}
      <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-primary/20 to-transparent" />
      <div className="w-full px-6 md:px-10 lg:px-16 max-w-6xl mx-auto relative z-10">
        <div className="text-center mb-12 md:mb-16">
          <p className="font-body text-xs tracking-[0.25em] uppercase text-primary mb-3">
            Nossa História
          </p>
          <h2 className="font-display text-2xl md:text-3xl lg:text-4xl text-brown">
            Momentos que marcam nossa história
          </h2>
        </div>

        {error && (
          <div className="text-center py-8">
            <p className="text-brown/70 font-body">Não foi possível carregar os eventos.</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 font-body text-sm text-primary hover:underline cursor-pointer"
            >
              Tentar novamente
            </button>
          </div>
        )}

        {loading && !error && (
          <div className="flex gap-3 md:gap-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex-1 aspect-[4/5] bg-background-100 rounded-xl animate-pulse" />
            ))}
          </div>
        )}

        {!loading && !error && (
          <>
            {/* Navigation arrows */}
            {showNav && (
              <div className="flex items-center justify-end gap-2 mb-4">
                <button
                  onClick={prev}
                  disabled={currentIndex === 0}
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-beige border border-nude text-brown hover:bg-primary hover:text-white hover:border-primary transition-all disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                  aria-label="Anterior"
                >
                  <i className="ri-arrow-left-s-line text-lg" />
                </button>
                <button
                  onClick={next}
                  disabled={currentIndex >= maxIndex}
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-beige border border-nude text-brown hover:bg-primary hover:text-white hover:border-primary transition-all disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                  aria-label="Próximo"
                >
                  <i className="ri-arrow-right-s-line text-lg" />
                </button>
              </div>
            )}

            {/* Carousel track */}
            <div className="relative overflow-hidden rounded-xl" style={{ touchAction: "pan-y" }} {...swipe.handlers}>
              <div
                ref={trackRef}
                className="flex gap-3 md:gap-4 transition-transform duration-500 ease-out"
                style={{
                  transform: `translateX(-${currentIndex * (100 / cardsVisible)}%)`,
                }}
              >
                {events.map((event) => (
                  <Link
                    to={`/evento/${event.slug}`}
                    key={event.id}
                    className="group relative overflow-hidden rounded-xl cursor-pointer block shrink-0"
                    style={{ width: `calc(${100 / cardsVisible}% - ${(cardsVisible - 1) * 16 / cardsVisible}px)` }}
                    onClick={(e) => {
                      if (swipe.arrastou.current) { swipe.arrastou.current = false; e.preventDefault(); }
                    }}
                  >
                    <div className="aspect-[4/5] w-full overflow-hidden">
                      <img
                        src={event.main_image}
                        alt={event.caption}
                        className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
                      />
                    </div>
                    {/* Título sempre visível na capa (antes só aparecia ao passar o mouse, e no celular quase nunca) */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-transparent group-hover:from-black/85 transition-colors duration-500 flex items-end p-5 md:p-6">
                      <div className="w-full">
                        {event.date && (
                          <p className="font-body text-[11px] tracking-[0.2em] uppercase text-white/75 mb-2">
                            {event.date}
                          </p>
                        )}
                        <h3 className="font-display text-xl md:text-2xl text-white leading-tight mb-3">
                          {event.title}
                        </h3>
                        {event.location && (
                          <p className="font-body text-xs text-white/75 flex items-center gap-1 mb-4">
                            <i className="ri-map-pin-line" />
                            <span>{event.location}</span>
                          </p>
                        )}
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-white/90 text-brown px-4 py-1.5 font-body text-xs group-hover:bg-primary group-hover:text-white transition-colors">
                          Ver fotos e vídeos
                          <i className="ri-arrow-right-line" />
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Dots */}
            {showNav && maxIndex > 0 && (
              <div className="flex items-center justify-center gap-2 mt-6">
                {Array.from({ length: maxIndex + 1 }).map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentIndex(i)}
                    className={`w-2 h-2 rounded-full transition-all duration-300 cursor-pointer ${
                      i === currentIndex ? "bg-primary w-6" : "bg-primary/30 hover:bg-primary/50"
                    }`}
                    aria-label={`Ir para slide ${i + 1}`}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}